import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronRight, 
  AlertCircle, 
  X, 
  Plus, 
  Trash2, 
  Search,
  Package,
  Settings,
  CheckCircle2,
  ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useNavigation } from "@/components/layout/app-layout";
import { BOM, BOMComponent, BOMType, BOMStatus } from "@/types/bom";
import { MOCK_BOMS } from "@/data/mockBOMs";
import { generateMockStockItems } from "@/data/mockStockItems";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

interface ValidationErrors {
  [key: string]: string;
}

interface CreateBOMPageProps {
  onBOMCreated?: (bom: BOM) => void;
}

export function CreateBOMPage({ onBOMCreated }: CreateBOMPageProps) {
  const { navigateTo, pageParams } = useNavigation();
  const editMode = pageParams?.editMode || false;
  const editBOMData = pageParams?.bomData as BOM | undefined;
  const [currentStep, setCurrentStep] = useState(1);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [showComponentPicker, setShowComponentPicker] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [componentSearchQuery, setComponentSearchQuery] = useState("");
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);

  // Get available stock items
  const stockItems = generateMockStockItems();

  // Initialize new BOM with defaults or edit data
  const [newBOM, setNewBOM] = useState<Partial<BOM>>(() => {
    if (editMode && editBOMData) {
      return editBOMData;
    }
    return {
      bomNumber: `BOM-${String(MOCK_BOMS.length + 1).padStart(3, '0')}`,
      parentItemCode: "",
      parentItemName: "",
      parentItemDescription: "",
      version: "1.0",
      status: "draft" as BOMStatus,
      effectiveDate: new Date(),
      expiryDate: null,
      bomType: "assembly" as BOMType,
      baseQuantity: 1,
      unitOfMeasure: "Each",
      components: [],
      totalMaterialCost: 0,
      totalCost: 0,
      costPerUnit: 0,
      notes: "",
      isMultiLevel: false,
      bomLevel: 0,
    };
  });

  // Check for draft on mount (skip if in edit mode)
  useEffect(() => {
    if (editMode) return; // Don't restore draft when editing existing BOM
    
    const draft = localStorage.getItem("bomDraft");
    if (draft) {
      try {
        const parsedDraft = JSON.parse(draft);
        const shouldRestore = window.confirm("You have an unsaved draft. Would you like to restore it?");
        if (shouldRestore) {
          setNewBOM(parsedDraft);
          setHasUnsavedChanges(true);
          toast.info("Draft restored");
        } else {
          localStorage.removeItem("bomDraft");
        }
      } catch (error) {
        console.error("Failed to parse draft:", error);
        localStorage.removeItem("bomDraft");
      }
    }
  }, [editMode]);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const interval = setInterval(() => {
      localStorage.setItem("bomDraft", JSON.stringify(newBOM));
    }, 30000);

    return () => clearInterval(interval);
  }, [newBOM, hasUnsavedChanges]);

  // Calculate costs whenever components change
  useEffect(() => {
    const materialCost = (newBOM.components || []).reduce((sum, comp) => sum + comp.totalCost, 0);

    setNewBOM(prev => ({
      ...prev,
      totalMaterialCost: materialCost,
      totalCost: materialCost,
      costPerUnit: materialCost / (prev.baseQuantity || 1),
    }));
  }, [newBOM.components, newBOM.baseQuantity]);

  // Validate field
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "bomNumber":
        if (!value || value.trim() === "") return "BOM number is required";
        // Skip uniqueness check in edit mode or if it's the same BOM number
        if (!editMode || (editBOMData && value !== editBOMData.bomNumber)) {
          if (MOCK_BOMS.some(bom => bom.bomNumber.toLowerCase() === value.toLowerCase())) {
            return "This BOM number already exists";
          }
        }
        return "";
      
      case "parentItemCode":
        if (!value || value.trim() === "") return "Parent item is required";
        return "";
      
      case "version":
        if (!value || value.trim() === "") return "Version is required";
        if (!/^\d+\.\d+$/.test(value)) return "Version must be in format X.Y (e.g., 1.0)";
        return "";
      
      case "baseQuantity":
        if (!value || value <= 0) return "Base quantity must be greater than 0";
        return "";
      
      case "effectiveDate":
        if (!value) return "Effective date is required";
        return "";
      
      default:
        return "";
    }
  };

  // Handle field change
  const handleFieldChange = (field: keyof BOM, value: any) => {
    setNewBOM(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    
    // Mark field as touched
    setTouchedFields(prev => new Set(prev).add(field));
    
    // Validate field
    const error = validateField(field, value);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  // Handle field blur
  const handleFieldBlur = (field: keyof BOM) => {
    setTouchedFields(prev => new Set(prev).add(field));
    const error = validateField(field, newBOM[field]);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  // Handle parent item selection
  const handleSelectParentItem = (itemCode: string) => {
    const item = stockItems.find(i => i.code === itemCode);
    if (item) {
      setNewBOM(prev => ({
        ...prev,
        parentItemCode: item.code,
        parentItemName: item.description,
        parentItemDescription: item.description,
        unitOfMeasure: item.unitOfMeasure || "Each",
      }));
      setHasUnsavedChanges(true);
      setShowItemPicker(false);
      setItemSearchQuery("");
    }
  };

  // Add component(s)
  const handleAddComponents = () => {
    if (selectedComponents.length === 0) return;

    const newComponents: BOMComponent[] = selectedComponents.map((itemCode, index) => {
      const item = stockItems.find(i => i.code === itemCode);
      if (!item) return null;

      return {
        id: `c${Date.now()}-${index}`,
        lineNumber: ((newBOM.components?.length || 0) + index + 1) * 10,
        componentCode: item.code,
        componentName: item.description,
        componentDescription: item.description,
        quantity: 1,
        unitOfMeasure: item.unitOfMeasure || "Each",
        unitCost: item.unitPrice || 0,
        totalCost: item.unitPrice || 0,
        scrapPercentage: 0,
        isOptional: false,
        leadTimeDays: item.leadTimeDays,
        supplier: item.supplier,
        availableQuantity: item.physicalQuantity,
      };
    }).filter(Boolean) as BOMComponent[];

    setNewBOM(prev => ({
      ...prev,
      components: [...(prev.components || []), ...newComponents],
    }));
    setHasUnsavedChanges(true);
    setShowComponentPicker(false);
    setComponentSearchQuery("");
    setSelectedComponents([]);
    
    toast.success(`Added ${newComponents.length} component${newComponents.length > 1 ? 's' : ''}`);
  };

  // Toggle component selection
  const toggleComponentSelection = (itemCode: string) => {
    setSelectedComponents(prev => 
      prev.includes(itemCode) 
        ? prev.filter(code => code !== itemCode)
        : [...prev, itemCode]
    );
  };

  // Update component
  const handleUpdateComponent = (id: string, field: keyof BOMComponent, value: any) => {
    setNewBOM(prev => ({
      ...prev,
      components: (prev.components || []).map(comp => {
        if (comp.id === id) {
          const updated = { ...comp, [field]: value };
          // Recalculate total cost
          if (field === "quantity" || field === "unitCost") {
            updated.totalCost = updated.quantity * updated.unitCost;
          }
          return updated;
        }
        return comp;
      }),
    }));
    setHasUnsavedChanges(true);
  };

  // Remove component
  const handleRemoveComponent = (id: string) => {
    setNewBOM(prev => ({
      ...prev,
      components: (prev.components || []).filter(comp => comp.id !== id),
    }));
    setHasUnsavedChanges(true);
  };

  // Validate step
  const validateStep = (step: number): boolean => {
    const errors: ValidationErrors = {};

    if (step === 1) {
      // Basic Details
      ["bomNumber", "parentItemCode", "version", "baseQuantity", "effectiveDate"].forEach(field => {
        const error = validateField(field, newBOM[field as keyof BOM]);
        if (error) errors[field] = error;
      });
    } else if (step === 2) {
      // Components - at least one required
      if (!newBOM.components || newBOM.components.length === 0) {
        errors.components = "At least one component is required";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle next step
  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    } else {
      toast.error("Please fix the errors before continuing");
    }
  };

  // Handle previous step
  const handlePreviousStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Handle save
  const handleSave = async () => {
    // Validate all steps
    if (!validateStep(1) || !validateStep(2)) {
      toast.error("Please fix all errors before saving");
      setCurrentStep(1);
      return;
    }

    setIsSaving(true);

    try {
      const finalBOM: BOM = {
        id: editMode && editBOMData ? editBOMData.id : String(MOCK_BOMS.length + 1),
        bomNumber: newBOM.bomNumber!,
        parentItemCode: newBOM.parentItemCode!,
        parentItemName: newBOM.parentItemName!,
        parentItemDescription: newBOM.parentItemDescription!,
        version: newBOM.version!,
        status: newBOM.status!,
        effectiveDate: newBOM.effectiveDate!,
        expiryDate: newBOM.expiryDate || null,
        bomType: newBOM.bomType!,
        baseQuantity: newBOM.baseQuantity!,
        unitOfMeasure: newBOM.unitOfMeasure!,
        components: newBOM.components!,
        totalMaterialCost: newBOM.totalMaterialCost!,
        totalCost: newBOM.totalCost!,
        costPerUnit: newBOM.costPerUnit!,
        createdBy: editMode && editBOMData ? editBOMData.createdBy : "Current User",
        createdDate: editMode && editBOMData ? editBOMData.createdDate : new Date(),
        lastModifiedBy: "Current User",
        lastModifiedDate: new Date(),
        notes: newBOM.notes,
        usageCount: editMode && editBOMData ? editBOMData.usageCount : 0,
        lastUsedDate: editMode && editBOMData ? editBOMData.lastUsedDate : null,
        isMultiLevel: newBOM.isMultiLevel!,
        bomLevel: newBOM.bomLevel!,
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      if (editMode && editBOMData) {
        // Update existing BOM in mock data
        const index = MOCK_BOMS.findIndex(bom => bom.id === editBOMData.id);
        if (index !== -1) {
          MOCK_BOMS[index] = finalBOM;
        }
        toast.success(`BOM ${finalBOM.bomNumber} updated successfully`);
      } else {
        // Add new BOM to mock data
        MOCK_BOMS.push(finalBOM);
        toast.success(`BOM ${finalBOM.bomNumber} created successfully`);
      }

      // Clear draft
      localStorage.removeItem("bomDraft");

      // Call callback if provided
      onBOMCreated?.(finalBOM);

      // Navigate back to BOMs page
      navigateTo("bills-of-material");
    } catch (error) {
      toast.error(editMode ? "Failed to update BOM" : "Failed to create BOM");
      console.error("Error saving BOM:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      navigateTo("bills-of-material");
    }
  };

  // Filter stock items for parent item picker
  // Only show items that can be assembled from components (isAssembly: true)
  const filteredStockItems = stockItems.filter(item =>
    item.isAssembly && // Only show items with "Can be assembled from components" enabled
    (item.code.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(itemSearchQuery.toLowerCase()))
  );

  // Filter stock items for component picker (only show items marked as components, exclude parent item)
  const filteredComponentItems = stockItems.filter(item =>
    item.isComponent && // Only show items that can be used in assemblies
    item.code !== newBOM.parentItemCode &&
    (item.code.toLowerCase().includes(componentSearchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(componentSearchQuery.toLowerCase()))
  );

  // Get error count
  const errorCount = Object.values(validationErrors).filter(e => e !== "").length;

  // Step indicator
  const steps = [
    { number: 1, title: "Basic Details", icon: Settings },
    { number: 2, title: "Components", icon: Package },
    { number: 3, title: "Review", icon: CheckCircle2 },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleCancel}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">
              {editMode ? `Edit BOM - ${newBOM.bomNumber}` : "Create New BOM"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {editMode ? "Update the bill of materials" : "Define a bill of materials for manufacturing or assembly"}
            </p>
          </div>
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            {newBOM.status}
          </Badge>
        </div>
      </div>

      {/* Step Indicator */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors ${
                    currentStep === step.number
                      ? "border-primary bg-primary text-primary-foreground"
                      : currentStep > step.number
                      ? "border-green-500 bg-green-500 text-white"
                      : "border-muted-foreground/25 bg-background text-muted-foreground"
                  }`}
                >
                  {currentStep > step.number ? (
                    <CheckCircle2 className="h-6 w-6" />
                  ) : (
                    <step.icon className="h-6 w-6" />
                  )}
                </div>
                <p
                  className={`mt-2 text-sm font-medium ${
                    currentStep === step.number
                      ? "text-primary"
                      : currentStep > step.number
                      ? "text-green-600"
                      : "text-muted-foreground"
                  }`}
                >
                  {step.title}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`h-0.5 flex-1 mx-4 transition-colors ${
                    currentStep > step.number ? "bg-green-500" : "bg-muted-foreground/25"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Error Summary */}
      {errorCount > 0 && touchedFields.size > 0 && (
        <Card className="p-4 border-destructive bg-destructive/5">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
            <div>
              <p className="font-medium text-destructive">
                Please fix {errorCount} error{errorCount > 1 ? 's' : ''} before continuing
              </p>
              <ul className="mt-2 space-y-1 text-sm text-destructive/80">
                {Object.entries(validationErrors).map(([field, error]) => 
                  error && (
                    <li key={field}>• {error}</li>
                  )
                )}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Form Content */}
      <Card className="p-6">
        {/* Step 1: Basic Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Basic Details</h2>

            {/* BOM Number */}
            <div className="space-y-2">
              <Label htmlFor="bomNumber" className="flex items-center gap-1">
                BOM Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="bomNumber"
                value={newBOM.bomNumber}
                onChange={(e) => handleFieldChange("bomNumber", e.target.value)}
                onBlur={() => handleFieldBlur("bomNumber")}
                placeholder="BOM-001"
                className={validationErrors.bomNumber && touchedFields.has("bomNumber") ? "border-destructive" : ""}
              />
              {validationErrors.bomNumber && touchedFields.has("bomNumber") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.bomNumber}
                </p>
              )}
            </div>

            {/* Parent Item */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Parent Item <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newBOM.parentItemCode ? `${newBOM.parentItemCode} - ${newBOM.parentItemName}` : ""}
                  placeholder="Select parent item"
                  readOnly
                  className={`flex-1 ${validationErrors.parentItemCode && touchedFields.has("parentItemCode") ? "border-destructive" : ""}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowItemPicker(true)}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Browse
                </Button>
              </div>
              {validationErrors.parentItemCode && touchedFields.has("parentItemCode") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.parentItemCode}
                </p>
              )}
            </div>

            {/* Version */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="version" className="flex items-center gap-1">
                  Version <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="version"
                  value={newBOM.version}
                  onChange={(e) => handleFieldChange("version", e.target.value)}
                  onBlur={() => handleFieldBlur("version")}
                  placeholder="1.0"
                  className={validationErrors.version && touchedFields.has("version") ? "border-destructive" : ""}
                />
                {validationErrors.version && touchedFields.has("version") && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <X className="h-3 w-3" />
                    {validationErrors.version}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={newBOM.status}
                  onValueChange={(value: BOMStatus) => handleFieldChange("status", value)}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* BOM Type */}
            <div className="space-y-2">
              <Label htmlFor="bomType">BOM Type</Label>
              <Select
                value={newBOM.bomType}
                onValueChange={(value: BOMType) => handleFieldChange("bomType", value)}
              >
                <SelectTrigger id="bomType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="assembly">Assembly</SelectItem>
                  <SelectItem value="kit">Kit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Base Quantity and UOM */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="baseQuantity" className="flex items-center gap-1">
                  Base Quantity <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="baseQuantity"
                  type="number"
                  value={newBOM.baseQuantity}
                  onChange={(e) => handleFieldChange("baseQuantity", parseFloat(e.target.value) || 0)}
                  onBlur={() => handleFieldBlur("baseQuantity")}
                  min="0"
                  step="1"
                  className={validationErrors.baseQuantity && touchedFields.has("baseQuantity") ? "border-destructive" : ""}
                />
                {validationErrors.baseQuantity && touchedFields.has("baseQuantity") && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <X className="h-3 w-3" />
                    {validationErrors.baseQuantity}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="unitOfMeasure">Unit of Measure</Label>
                <Select
                  value={newBOM.unitOfMeasure}
                  onValueChange={(value) => handleFieldChange("unitOfMeasure", value)}
                >
                  <SelectTrigger id="unitOfMeasure">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Each">Each</SelectItem>
                    <SelectItem value="Box/Carton">Box/Carton</SelectItem>
                    <SelectItem value="Kg">Kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="Litre">Litre</SelectItem>
                    <SelectItem value="Metre">Metre</SelectItem>
                    <SelectItem value="Pallet">Pallet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Effective Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="effectiveDate" className="flex items-center gap-1">
                  Effective Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="effectiveDate"
                  type="date"
                  value={newBOM.effectiveDate ? new Date(newBOM.effectiveDate).toISOString().split('T')[0] : ""}
                  onChange={(e) => handleFieldChange("effectiveDate", new Date(e.target.value))}
                  onBlur={() => handleFieldBlur("effectiveDate")}
                  className={validationErrors.effectiveDate && touchedFields.has("effectiveDate") ? "border-destructive" : ""}
                />
                {validationErrors.effectiveDate && touchedFields.has("effectiveDate") && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <X className="h-3 w-3" />
                    {validationErrors.effectiveDate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date (Optional)</Label>
                <Input
                  id="expiryDate"
                  type="date"
                  value={newBOM.expiryDate ? new Date(newBOM.expiryDate).toISOString().split('T')[0] : ""}
                  onChange={(e) => handleFieldChange("expiryDate", e.target.value ? new Date(e.target.value) : null)}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={newBOM.notes}
                onChange={(e) => handleFieldChange("notes", e.target.value)}
                placeholder="Add any additional notes about this BOM"
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Step 2: Components */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Parent Item Context */}
            {newBOM.parentItemCode && (
              <Card className="p-4 bg-muted/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Building BOM for</p>
                    <p className="font-semibold text-lg">
                      {newBOM.parentItemCode} - {newBOM.parentItemName}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Base Quantity: {newBOM.baseQuantity} {newBOM.unitOfMeasure} • Type: {newBOM.bomType === 'assembly' ? 'Assembly' : 'Kit'}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Components</h2>
              <Button onClick={() => setShowComponentPicker(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Component
              </Button>
            </div>

            {validationErrors.components && (
              <Card className="p-4 border-destructive bg-destructive/5">
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.components}
                </p>
              </Card>
            )}

            {newBOM.components && newBOM.components.length > 0 ? (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Line</TableHead>
                      <TableHead>Component Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-24">Qty</TableHead>
                      <TableHead className="w-24">UOM</TableHead>
                      <TableHead className="w-32">Unit Cost</TableHead>
                      <TableHead className="w-32">Total Cost</TableHead>
                      <TableHead className="w-24">Scrap %</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newBOM.components.map((component) => (
                      <TableRow key={component.id}>
                        <TableCell>{component.lineNumber}</TableCell>
                        <TableCell className="font-medium">{component.componentCode}</TableCell>
                        <TableCell>{component.componentName}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={component.quantity}
                            onChange={(e) => handleUpdateComponent(component.id, "quantity", parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                            className="w-20"
                          />
                        </TableCell>
                        <TableCell>{component.unitOfMeasure}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={component.unitCost}
                            onChange={(e) => handleUpdateComponent(component.id, "unitCost", parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                            className="w-28"
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          £{component.totalCost.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={component.scrapPercentage}
                            onChange={(e) => handleUpdateComponent(component.id, "scrapPercentage", parseFloat(e.target.value) || 0)}
                            min="0"
                            max="100"
                            step="1"
                            className="w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveComponent(component.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <Card className="p-12 text-center border-dashed">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No components added yet</p>
                <Button onClick={() => setShowComponentPicker(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Component
                </Button>
              </Card>
            )}
          </div>
        )}

        {/* Step 3: Review */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Review & Confirm</h2>

            {/* Basic Details Summary */}
            <Card className="p-4">
              <h3 className="font-medium mb-4">Basic Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">BOM Number</p>
                  <p className="font-medium">{newBOM.bomNumber}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Version</p>
                  <p className="font-medium">{newBOM.version}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Parent Item</p>
                  <p className="font-medium">{newBOM.parentItemCode} - {newBOM.parentItemName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">BOM Type</p>
                  <p className="font-medium capitalize">{newBOM.bomType}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Base Quantity</p>
                  <p className="font-medium">{newBOM.baseQuantity} {newBOM.unitOfMeasure}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge variant="outline" className="capitalize">{newBOM.status}</Badge>
                </div>
              </div>
            </Card>

            {/* Components Summary */}
            <Card className="p-4">
              <h3 className="font-medium mb-4">Components ({newBOM.components?.length || 0})</h3>
              {newBOM.components && newBOM.components.length > 0 ? (
                <div className="space-y-2">
                  {newBOM.components.map((comp) => (
                    <div key={comp.id} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                      <div>
                        <p className="font-medium">{comp.componentCode}</p>
                        <p className="text-muted-foreground text-xs">{comp.componentName}</p>
                      </div>
                      <div className="text-right">
                        <p>{comp.quantity} {comp.unitOfMeasure}</p>
                        <p className="text-muted-foreground text-xs">£{comp.totalCost.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No components</p>
              )}
            </Card>

            {/* Cost Summary */}
            <Card className="p-4 bg-primary/5">
              <h3 className="font-medium mb-4">Cost Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Material Cost</span>
                  <span className="font-medium">£{newBOM.totalMaterialCost?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-semibold">Total Cost</span>
                  <span className="font-semibold text-lg">£{newBOM.totalCost?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Cost per Unit (Base Qty: {newBOM.baseQuantity})</span>
                  <span>£{newBOM.costPerUnit?.toFixed(2) || "0.00"}</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t">
          <Button
            variant="outline"
            onClick={handlePreviousStep}
            disabled={currentStep === 1}
          >
            Previous
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            {currentStep < 3 ? (
              <Button onClick={handleNextStep}>
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update BOM" : "Create BOM")}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Parent Item Picker Dialog */}
      <Dialog open={showItemPicker} onOpenChange={setShowItemPicker}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Parent Item</DialogTitle>
            <DialogDescription>
              Choose the item that this BOM will produce
            </DialogDescription>
          </DialogHeader>
          <Command className="rounded-lg border">
            <CommandInput
              placeholder="Search items..."
              value={itemSearchQuery}
              onValueChange={setItemSearchQuery}
            />
            <CommandList>
              <CommandEmpty>No items found.</CommandEmpty>
              <CommandGroup>
                {filteredStockItems.slice(0, 20).map((item) => (
                  <CommandItem
                    key={item.code}
                    value={item.code}
                    onSelect={() => handleSelectParentItem(item.code)}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <p className="font-medium">{item.code}</p>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                      <Badge variant="outline">{item.category}</Badge>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      {/* Component Picker Dialog */}
      <Dialog open={showComponentPicker} onOpenChange={(open) => {
        setShowComponentPicker(open);
        if (!open) {
          setSelectedComponents([]);
          setComponentSearchQuery("");
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Components</DialogTitle>
            <DialogDescription>
              Select one or more items to add as components
            </DialogDescription>
          </DialogHeader>
          <Command className="rounded-lg border">
            <CommandInput
              placeholder="Search items..."
              value={componentSearchQuery}
              onValueChange={setComponentSearchQuery}
            />
            <CommandList>
              <CommandEmpty>No items found.</CommandEmpty>
              <CommandGroup>
                {filteredComponentItems.slice(0, 20).map((item) => (
                  <CommandItem
                    key={item.code}
                    value={item.code}
                    onSelect={() => toggleComponentSelection(item.code)}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <Checkbox
                        checked={selectedComponents.includes(item.code)}
                        onCheckedChange={() => toggleComponentSelection(item.code)}
                      />
                      <div className="flex items-center justify-between flex-1">
                        <div>
                          <p className="font-medium">{item.code}</p>
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">£{item.unitPrice?.toFixed(2) || "0.00"}</p>
                          <p className="text-xs text-muted-foreground">{item.physicalQuantity} available</p>
                        </div>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              {selectedComponents.length} component{selectedComponents.length !== 1 ? 's' : ''} selected
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowComponentPicker(false);
                  setSelectedComponents([]);
                  setComponentSearchQuery("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddComponents}
                disabled={selectedComponents.length === 0}
              >
                Add Selected ({selectedComponents.length})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigateTo("bills-of-material")}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
